// Resident Portal — useResidentFeedbackEligibility
// Combines all eligibility checks for resident valet feedback.
//
// Safe defaults:
// - If getLocationFeatureSettings fails: eligible = false
// - If still loading: eligible = false, loading = true
// - Never assume eligible

import { useState, useEffect, useRef } from "react";
import { functions, auth } from "../firebase";
import { httpsCallable, type HttpsCallableResult } from "firebase/functions";

// ── Feature flag types (mirror backend shape) ───────────────────────────────

interface ResidentValetFeedbackFlags {
  enabled: boolean;
  portalEnabled: boolean;
  requireCompletedService: boolean;
  allowComment: boolean;
  allowTags: boolean;
  allowEmployeeRating: boolean;
  allowAnonymousResidentFeedback: boolean;
  feedbackWindowHours: number;
}

interface LocationFeatureSettingsData {
  residentValetFeedback?: Partial<ResidentValetFeedbackFlags>;
}

// ── Hook params and return type ─────────────────────────────────────────────

export interface ResidentFeedbackEligibilityParams {
  locationId: string;
  serviceId?: string;       // checkinId or valetRequestId
  serviceType?: string;     // "retrieval" | "checkin" | etc.
  serviceStatus?: string;   // e.g. "completed" — checked against requireCompletedService
  serviceCompletedAt?: string | null;  // ISO date string — used to check feedback window
  alreadySubmitted?: boolean;          // caller knows if feedback was already submitted
}

export interface ResidentFeedbackEligibilityResult {
  eligible: boolean;
  reason: string | null;
  loading: boolean;
  flags: ResidentValetFeedbackFlags | null;
}

// ── Module-level in-memory cache (per locationId) ────────────────────────────
// Keyed by locationId. Populated on first successful fetch.
// Cleared on module unload (page refresh / logout).

const _featureFlagCache: Map<string, ResidentValetFeedbackFlags> = new Map();

function defaultFlags(): ResidentValetFeedbackFlags {
  return {
    enabled: false,
    portalEnabled: false,
    requireCompletedService: false,
    allowComment: false,
    allowTags: false,
    allowEmployeeRating: false,
    allowAnonymousResidentFeedback: false,
    feedbackWindowHours: 72,
  };
}

function parseFlags(data: LocationFeatureSettingsData): ResidentValetFeedbackFlags {
  const raw = data?.residentValetFeedback ?? {};
  return {
    enabled: raw.enabled ?? false,
    portalEnabled: raw.portalEnabled ?? false,
    requireCompletedService: raw.requireCompletedService ?? false,
    allowComment: raw.allowComment ?? false,
    allowTags: raw.allowTags ?? false,
    allowEmployeeRating: raw.allowEmployeeRating ?? false,
    allowAnonymousResidentFeedback: raw.allowAnonymousResidentFeedback ?? false,
    feedbackWindowHours: raw.feedbackWindowHours ?? 72,
  };
}

// ── Main hook ────────────────────────────────────────────────────────────────

export function useResidentFeedbackEligibility(
  params: ResidentFeedbackEligibilityParams
): ResidentFeedbackEligibilityResult {
  const {
    locationId,
    serviceId,
    serviceStatus,
    serviceCompletedAt,
    alreadySubmitted = false,
  } = params;

  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState<ResidentValetFeedbackFlags | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;

    if (!locationId) {
      setLoading(false);
      setFlags(null);
      setFetchError(true);
      return;
    }

    // Check cache first
    const cached = _featureFlagCache.get(locationId);
    if (cached) {
      if (!abortRef.current) {
        setFlags(cached);
        setLoading(false);
        setFetchError(false);
      }
      return;
    }

    // Fetch from backend
    const user = auth.currentUser;
    if (!user) {
      if (!abortRef.current) {
        setLoading(false);
        setFlags(null);
        setFetchError(true);
      }
      return;
    }

    setLoading(true);
    setFetchError(false);

    const getFeatureSettings = httpsCallable(functions, "getLocationFeatureSettings");

    getFeatureSettings({ locationId })
      .then((result: HttpsCallableResult<any>) => {
        if (abortRef.current) return;

        const data = result?.data;
        const ok = data?.ok === true;
        if (!ok) {
          console.error("FEEDBACK_FLAGS_FETCH_ERROR", data?.error);
          setFetchError(true);
          setLoading(false);
          setFlags(null);
          return;
        }

        // Unwrap data envelope: { ok: true, data: { residentValetFeedback: {...} } }
        const inner: LocationFeatureSettingsData = data?.data ?? {};
        const parsed = parseFlags(inner);

        _featureFlagCache.set(locationId, parsed);
        setFlags(parsed);
        setLoading(false);
        setFetchError(false);

        console.log("FEEDBACK_FLAGS_LOADED", {
          locationId,
          enabled: parsed.enabled,
          portalEnabled: parsed.portalEnabled,
        });
      })
      .catch((err: unknown) => {
        if (abortRef.current) return;
        console.error("FEEDBACK_FLAGS_FETCH_EXCEPTION", err);
        setFetchError(true);
        setLoading(false);
        setFlags(null);
      });

    return () => {
      abortRef.current = true;
    };
  }, [locationId]);

  // ── Compute eligibility ────────────────────────────────────────────────────

  if (loading) {
    return { eligible: false, reason: null, loading: true, flags: null };
  }

  // If we failed to load flags, hide everything — safe default
  if (fetchError || flags === null) {
    return {
      eligible: false,
      reason: "Feedback is not available for this location",
      loading: false,
      flags: null,
    };
  }

  // Check 1: Feature enabled at all
  if (!flags.enabled) {
    return {
      eligible: false,
      reason: "Feedback is not available for this location",
      loading: false,
      flags,
    };
  }

  // Check 2: Portal channel enabled
  if (!flags.portalEnabled) {
    return {
      eligible: false,
      reason: "Portal feedback is not enabled for this location",
      loading: false,
      flags,
    };
  }

  // Check 3: requireCompletedService — service must be completed
  if (flags.requireCompletedService) {
    const isCompleted =
      serviceStatus?.toLowerCase() === "completed" ||
      serviceStatus?.toLowerCase() === "checked_out";
    if (!isCompleted) {
      return {
        eligible: false,
        reason: "Feedback must be linked to a completed service",
        loading: false,
        flags,
      };
    }
  }

  // Check 4: Feedback window
  if (serviceCompletedAt) {
    const completedTime = new Date(serviceCompletedAt).getTime();
    const now = Date.now();
    const windowMs = flags.feedbackWindowHours * 60 * 60 * 1000;
    if (!isNaN(completedTime) && now - completedTime > windowMs) {
      return {
        eligible: false,
        reason: "The feedback window has expired",
        loading: false,
        flags,
      };
    }
  }

  // Check 5: Already submitted
  if (alreadySubmitted) {
    return {
      eligible: false,
      reason: "You have already submitted feedback for this service",
      loading: false,
      flags,
    };
  }

  // All checks passed
  return { eligible: true, reason: null, loading: false, flags };
}

/**
 * Clear the feature flag cache.
 * Call on logout to ensure the next session re-fetches.
 */
export function clearResidentFeedbackFlagsCache(): void {
  _featureFlagCache.clear();
}
